import { useMemo, useState, type ChangeEvent } from "react";
import { Link } from "react-router";
import {
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { ErrorMessage } from "../components/auth/error-message.js";
import { ConfirmButton } from "../components/confirm-button.js";
import {
  Button,
  Card,
  Pill,
  SectionHeader,
} from "../components/design-system/index.js";
import { listAccounts } from "../lib/api-modules/accounts.js";
import {
  importMerrillBalances,
  importMerrillLots,
  importStatementRows,
  type MerrillReplacePlan,
} from "../lib/api-modules/imports.js";
import type { AccountSummary } from "../lib/api-modules/types.js";
import {
  STATEMENT_LABELS,
  includedByDefault,
  parseMerrillBalancesCsv,
  parseMerrillLotsCsv,
  parseStatementCsv,
  type MerrillAccountLots,
  type ParsedMerrillBalances,
  type ParsedMerrillLots,
  type ParsedStatement,
  type StatementRow,
} from "../lib/util/file-import.js";
import { formatCentsCurrency } from "../lib/util/money.js";
import { useDocumentTitle } from "../lib/util/use-document-title.js";

const INVESTMENT_TYPES = new Set([
  "taxable",
  "ira_traditional",
  "ira_roth",
  "ira_sep",
  "401k_traditional",
  "401k_roth",
  "hsa",
  "529",
  "utma",
]);
const SPENDING_TYPES = new Set(["credit_card", "checking", "savings"]);

/**
 * Refresh what an import changes. A blanket invalidate would also reload
 * the signed-in user, which remounts this page and loses its result.
 */
const IMPORTED_DATA_KEYS: ReadonlySet<unknown> = new Set([
  "accounts",
  "accounts-summary",
  "networth",
  "portfolio",
  "spending-transactions",
]);

function refreshFinancialData(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({
    predicate: (query) => IMPORTED_DATA_KEYS.has(query.queryKey[0]),
  });
}

const errorText = (err: unknown) =>
  err instanceof Error ? err.message : "Something went wrong.";

const KIND_LABELS: Readonly<Record<StatementRow["kind"], string>> = {
  spending: "Spending",
  refund: "Refund",
  transfer: "Transfer",
  deposit: "Deposit",
};

export function ImportPage() {
  useDocumentTitle("Import files");
  const accountsQuery = useQuery({
    queryKey: ["accounts"],
    queryFn: listAccounts,
  });
  const accounts = accountsQuery.data?.accounts ?? [];

  return (
    <div className="mx-auto max-w-5xl p-6">
      <SectionHeader
        title="Import files"
        sub="Download a file from your bank or brokerage, then drop it here. You'll see what changes before anything is saved."
      />
      <div className="mt-6 space-y-6">
        <MerrillSection accounts={accounts} />
        <MerrillBalancesSection accounts={accounts} />
        <StatementSection accounts={accounts} />
      </div>
    </div>
  );
}

// === Merrill ================================================================

function MerrillSection({ accounts }: { accounts: readonly AccountSummary[] }) {
  const queryClient = useQueryClient();
  const [parsed, setParsed] = useState<ParsedMerrillLots | null>(null);
  const [targets, setTargets] = useState<Record<string, string>>({});
  const [plans, setPlans] = useState<Record<string, MerrillReplacePlan>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const investmentAccounts = accounts.filter((a) =>
    INVESTMENT_TYPES.has(a.account_type),
  );

  async function onFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    setError(null);
    setPlans({});
    setDone(false);
    if (!file) return;
    try {
      const result = parseMerrillLotsCsv(await file.text());
      setParsed(result);
      const matched: Record<string, string> = {};
      for (const group of result.accounts) {
        const match = accounts.find(
          (a) => a.external_id === group.external_id,
        );
        if (match) matched[group.external_id] = match.account_id;
      }
      setTargets(matched);
    } catch (err) {
      setParsed(null);
      setError(errorText(err));
    }
  }

  const chosen = (parsed?.accounts ?? []).filter(
    (group) => targets[group.external_id],
  );

  function send(group: MerrillAccountLots, dryRun: boolean) {
    return importMerrillLots({
      account_id: targets[group.external_id]!,
      external_id: group.external_id,
      as_of_date: parsed?.as_of_date ?? new Date().toISOString().slice(0, 10),
      dry_run: dryRun,
      lots: group.lots,
    });
  }

  async function preview() {
    setBusy(true);
    setError(null);
    try {
      const next: Record<string, MerrillReplacePlan> = {};
      for (const group of chosen) {
        next[group.external_id] = await send(group, true);
      }
      setPlans(next);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function apply() {
    setBusy(true);
    setError(null);
    try {
      for (const group of chosen) await send(group, false);
      setDone(true);
      setPlans({});
      await refreshFinancialData(queryClient);
    } catch (err) {
      setError(
        `${errorText(err)} Upload the same file again to finish. Replacing is safe to repeat.`,
      );
    } finally {
      setBusy(false);
    }
  }

  const previewReady =
    chosen.length > 0 &&
    chosen.every((group) => plans[group.external_id] !== undefined);

  return (
    <Card>
      <h2 className="font-display text-lg font-medium">Merrill holdings</h2>
      <p className="mt-1 text-sm text-[var(--color-muted)]">
        In Merrill, download <strong>Unrealized Gain/Loss with Tax Lots</strong>{" "}
        as a CSV. This replaces the lots in each account you choose. The file
        has no cash or margin, so update those below as well.
      </p>
      <input
        aria-label="Merrill tax lots file"
        type="file"
        accept=".csv,text/csv"
        onChange={(event) => void onFile(event)}
        className="mt-4 block text-sm"
      />
      {error && <div className="mt-3"><ErrorMessage>{error}</ErrorMessage></div>}
      {done && (
        <p className="mt-3 text-sm text-[var(--color-success)]">
          Holdings replaced. <Link to="/accounts" className="underline">See your accounts</Link>
        </p>
      )}

      {parsed && !done && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-[var(--color-muted)]">
            File dated {parsed.as_of_date ?? "unknown"}.
          </p>
          {parsed.accounts.map((group) => {
            const plan = plans[group.external_id];
            return (
              <div
                key={group.external_id}
                className="rounded-md border border-[var(--color-border-subtle)] p-3 text-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p>
                    <span className="font-medium">
                      {group.nickname || group.external_id}
                    </span>{" "}
                    <span className="text-[var(--color-muted)]">
                      {group.lots.length} lots
                    </span>
                  </p>
                  <select
                    aria-label={`Hearth account for ${group.external_id}`}
                    value={targets[group.external_id] ?? ""}
                    onChange={(event) => {
                      setPlans({});
                      setTargets({
                        ...targets,
                        [group.external_id]: event.target.value,
                      });
                    }}
                    className="rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] px-2 py-1"
                  >
                    <option value="">Skip this account</option>
                    {investmentAccounts.map((a) => (
                      <option key={a.account_id} value={a.account_id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </div>
                {plan && (
                  <p className="mt-2 text-[var(--color-muted)]">
                    Replaces {plan.existing_lot_count} lots costing{" "}
                    {formatCentsCurrency(plan.existing_cost_basis_cents)} with{" "}
                    {plan.new_lot_count} lots costing{" "}
                    {formatCentsCurrency(plan.new_cost_basis_cents)}.
                    {plan.tickers_added.length > 0 &&
                      ` New: ${plan.tickers_added.join(", ")}.`}
                    {plan.tickers_removed.length > 0 &&
                      ` Removed: ${plan.tickers_removed.join(", ")}.`}
                  </p>
                )}
              </div>
            );
          })}
          {parsed.warnings.length > 0 && (
            <details className="text-sm text-[var(--color-muted)]">
              <summary>
                {parsed.warnings.length} row
                {parsed.warnings.length === 1 ? "" : "s"} skipped
              </summary>
              <ul className="mt-1 list-disc pl-5">
                {parsed.warnings.map((w) => <li key={w}>{w}</li>)}
              </ul>
            </details>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="secondary"
              size="sm"
              disabled={busy || chosen.length === 0}
              onClick={() => void preview()}
            >
              Preview changes
            </Button>
            {previewReady && (
              <ConfirmButton
                label={`Replace holdings in ${chosen.length} ${chosen.length === 1 ? "account" : "accounts"}`}
                confirmLabel="Yes, replace"
                pendingLabel="Replacing…"
                isPending={busy}
                onConfirm={() => void apply()}
              />
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

// === Merrill balances =======================================================

function BalanceChange({
  label,
  before,
  after,
}: {
  label: string;
  before: number;
  after: number;
}) {
  return (
    <p className="text-[var(--color-muted)]">
      {label}: {formatCentsCurrency(before)} →{" "}
      <span className="text-[var(--color-text)]">{formatCentsCurrency(after)}</span>
    </p>
  );
}

function MerrillBalancesSection({
  accounts,
}: {
  accounts: readonly AccountSummary[];
}) {
  const queryClient = useQueryClient();
  const [parsed, setParsed] = useState<ParsedMerrillBalances | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function onFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    setError(null);
    setDone(false);
    setParsed(null);
    if (!file) return;
    try {
      setParsed(parseMerrillBalancesCsv(await file.text()));
    } catch (err) {
      setError(errorText(err));
    }
  }

  const rows = (parsed?.accounts ?? []).map((balances) => ({
    balances,
    account: accounts.find(
      (a) =>
        a.external_id === balances.external_id &&
        INVESTMENT_TYPES.has(a.account_type),
    ),
  }));
  const matched = rows.flatMap(({ balances, account }) =>
    account ? [{ balances, account }] : [],
  );

  async function apply() {
    if (!parsed) return;
    setBusy(true);
    setError(null);
    try {
      await importMerrillBalances({
        as_of_date: parsed.as_of_date,
        balances: matched.map(({ balances, account }) => ({
          account_id: account.account_id,
          cash_balance_cents: balances.cash_balance_cents,
          margin_balance_cents: balances.margin_balance_cents,
        })),
      });
      setDone(true);
      setParsed(null);
      await refreshFinancialData(queryClient);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <h2 className="font-display text-lg font-medium">Merrill cash and margin</h2>
      <p className="mt-1 text-sm text-[var(--color-muted)]">
        Export your Merrill Holdings page as a CSV. The right file is named{" "}
        <strong>ExportData</strong> and has a Balances section for each account.
        This updates cash and margin only, not your positions.
      </p>
      <input
        aria-label="Merrill balances file"
        type="file"
        accept=".csv,text/csv"
        onChange={(event) => void onFile(event)}
        className="mt-4 block text-sm"
      />
      {error && <div className="mt-3"><ErrorMessage>{error}</ErrorMessage></div>}
      {done && (
        <p className="mt-3 text-sm text-[var(--color-success)]">
          Cash and margin updated.{" "}
          <Link to="/accounts" className="underline">See your accounts</Link>
        </p>
      )}

      {parsed && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-[var(--color-muted)]">
            File dated {parsed.as_of_date}.
          </p>
          {rows.map(({ balances, account }) => (
            <div
              key={balances.external_id}
              className="rounded-md border border-[var(--color-border-subtle)] p-3 text-sm"
            >
              <p className="font-medium">{account?.name ?? balances.name}</p>
              {account ? (
                <>
                  <BalanceChange
                    label="Cash"
                    before={account.cash_balance_cents}
                    after={balances.cash_balance_cents}
                  />
                  <BalanceChange
                    label="Margin"
                    before={account.margin_balance_cents ?? 0}
                    after={balances.margin_balance_cents}
                  />
                </>
              ) : (
                <p className="text-[var(--color-muted)]">
                  Skipped: no investment account has the number{" "}
                  {balances.external_id}.
                </p>
              )}
            </div>
          ))}
          {parsed.warnings.length > 0 && (
            <ul className="list-disc pl-5 text-sm text-[var(--color-muted)]">
              {parsed.warnings.map((w) => <li key={w}>{w}</li>)}
            </ul>
          )}
          {matched.length > 0 && (
            <ConfirmButton
              label={`Update ${matched.length} ${matched.length === 1 ? "account" : "accounts"}`}
              confirmLabel="Yes, update"
              pendingLabel="Updating…"
              isPending={busy}
              onConfirm={() => void apply()}
            />
          )}
        </div>
      )}
    </Card>
  );
}

// === Bank and card statements ===============================================

function StatementSection({ accounts }: { accounts: readonly AccountSummary[] }) {
  const queryClient = useQueryClient();
  const [parsed, setParsed] = useState<ParsedStatement | null>(null);
  const [excluded, setExcluded] = useState<ReadonlySet<string>>(new Set());
  const [accountId, setAccountId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ added: number; skipped: number } | null>(null);

  const spendingAccounts = accounts.filter((a) =>
    SPENDING_TYPES.has(a.account_type),
  );

  async function onFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    setError(null);
    setResult(null);
    if (!file) return;
    try {
      const statement = parseStatementCsv(await file.text());
      setParsed(statement);
      setExcluded(
        new Set(
          statement.rows
            .filter((row) => !includedByDefault(row))
            .map((row) => row.source_ref),
        ),
      );
    } catch (err) {
      setParsed(null);
      setError(errorText(err));
    }
  }

  const selected = useMemo(
    () => (parsed?.rows ?? []).filter((r) => !excluded.has(r.source_ref)),
    [parsed, excluded],
  );
  const totalCents = selected.reduce((sum, r) => sum + r.amount_cents, 0);

  function toggle(ref: string) {
    const next = new Set(excluded);
    if (next.has(ref)) next.delete(ref);
    else next.add(ref);
    setExcluded(next);
  }

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      setResult(await importStatementRows(accountId, selected));
      setParsed(null);
      await refreshFinancialData(queryClient);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <h2 className="font-display text-lg font-medium">Bank and card spending</h2>
      <p className="mt-1 text-sm text-[var(--color-muted)]">
        Upload a CSV from Bank of America or American Express. Importing the
        same file twice won't add anything twice.
      </p>
      <input
        aria-label="Bank or card statement file"
        type="file"
        accept=".csv,text/csv"
        onChange={(event) => void onFile(event)}
        className="mt-4 block text-sm"
      />
      {error && <div className="mt-3"><ErrorMessage>{error}</ErrorMessage></div>}
      {result && (
        <p className="mt-3 text-sm text-[var(--color-success)]">
          Added {result.added} transactions
          {result.skipped > 0 && `, skipped ${result.skipped} already imported`}.{" "}
          <Link to="/spending" className="underline">See your spending</Link>
        </p>
      )}

      {parsed && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-[var(--color-muted)]">
            {STATEMENT_LABELS[parsed.format]}: {parsed.rows.length} rows.
            Transfers and deposits start unchecked, because a card payment is
            the same money as the card's own charges.
          </p>
          <select
            aria-label="Account for this file"
            value={accountId}
            onChange={(event) => setAccountId(event.target.value)}
            className="rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] px-2 py-1 text-sm"
          >
            <option value="">Choose an account…</option>
            {spendingAccounts.map((a) => (
              <option key={a.account_id} value={a.account_id}>{a.name}</option>
            ))}
          </select>
          {spendingAccounts.length === 0 && (
            <p className="text-sm text-[var(--color-muted)]">
              Add a card or bank account on the{" "}
              <Link to="/accounts" className="underline">Accounts page</Link> first.
            </p>
          )}
          <div className="max-h-96 overflow-auto rounded-md border border-[var(--color-border-subtle)]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[var(--color-elevated)] text-left text-xs text-[var(--color-muted)]">
                <tr>
                  <th className="p-2">Add</th>
                  <th className="p-2">Date</th>
                  <th className="p-2">Description</th>
                  <th className="p-2">Type</th>
                  <th className="p-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {parsed.rows.map((row) => (
                  <tr
                    key={row.source_ref}
                    className="border-t border-[var(--color-border-subtle)]"
                  >
                    <td className="p-2">
                      <input
                        type="checkbox"
                        aria-label={`Add ${row.merchant}`}
                        checked={!excluded.has(row.source_ref)}
                        onChange={() => toggle(row.source_ref)}
                      />
                    </td>
                    <td className="whitespace-nowrap p-2">{row.date}</td>
                    <td className="p-2">{row.merchant}</td>
                    <td className="p-2">
                      <Pill tone={includedByDefault(row) ? "neutral" : "warning"}>
                        {KIND_LABELS[row.kind]}
                      </Pill>
                    </td>
                    <td className="p-2 text-right font-mono">
                      {formatCentsCurrency(row.amount_cents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm">
              {selected.length} selected,{" "}
              <span className="font-mono">{formatCentsCurrency(totalCents)}</span>
            </p>
            <Button
              size="sm"
              disabled={busy || accountId === "" || selected.length === 0}
              onClick={() => void submit()}
            >
              {busy ? "Importing…" : `Import ${selected.length} transactions`}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
